import { css } from "@emotion/react";
import ColorHash from "color-hash";
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  format,
  subMonths,
  subYears,
} from "date-fns";
import Link from "next/link";
import { FC, useCallback, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import useIsSpMode from "~/lib/client/hooks/useIsSpMode";

type MonthlyFussyPoint = {
  yearAndMonth: string;
  fussyPoint: number;
  category: {
    id: number;
    name: string;
  };
};

type DailyFussyPoint = {
  date: string;
  fussyPoint: number;
  category: {
    id: number;
    name: string;
  };
};

type Props = {
  data: Array<MonthlyFussyPoint | DailyFussyPoint>;
};

const useVisibleCategories = (categories: string[]) => {
  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(categories)
  );

  const toggleCategory = useCallback((category: string) => {
    setVisibleCategories((prev) =>
      prev.has(category)
        ? new Set([...prev].filter((c) => c !== category))
        : new Set(prev).add(category)
    );
  }, []);

  return { visibleCategories, toggleCategory };
};

type DataByPeriod = {
  [periodKey: string]: {
    [categoryName: string]: number;
  };
};

type CompleteDataByPeriod = {
  [period: string]: {
    [categoryName: string]: number;
  };
};

const useChartData = (data: Array<MonthlyFussyPoint | DailyFussyPoint>) => {
  return useMemo(() => {
    if (data.length === 0) {
      return {
        chartData: [],
        categories: [],
      };
    }

    const isMonthlyData = "yearAndMonth" in data[0];
    const periodFormat = isMonthlyData ? "yyyy-MM" : "yyyy-MM-dd";

    // 月次データなら1年分、日次データなら3ヶ月分の期間を表示
    const periods = isMonthlyData
      ? eachMonthOfInterval({
          start: subYears(new Date(), 1),
          end: new Date(),
        })
      : eachDayOfInterval({
          start: subMonths(new Date(), 3),
          end: new Date(),
        });
    const formattedPeriods = periods.map((p) => format(p, periodFormat));

    // 使い回しやすいようにここで統一する
    const processedData = data.map(
      (
        item
      ): {
        periodKey: string;
        fussyPoint: number;
        category: {
          id: number;
          name: string;
        };
      } => {
        if ("yearAndMonth" in item) {
          return {
            periodKey: item.yearAndMonth,
            fussyPoint: item.fussyPoint,
            category: item.category,
          };
        } else {
          return {
            periodKey: item.date,
            fussyPoint: item.fussyPoint,
            category: item.category,
          };
        }
      }
    );

    const dataByPeriod = processedData.reduce((pre, cur) => {
      const { periodKey } = cur;

      if (!pre[periodKey]) {
        pre[periodKey] = {};
      }
      pre[periodKey][cur.category.name] = cur.fussyPoint;

      return pre;
    }, {} as DataByPeriod);

    const completeDataByPeriod = formattedPeriods.reduce((acc, period) => {
      acc[period] = dataByPeriod[period] || {};
      return acc;
    }, {} as CompleteDataByPeriod);

    const chartData = Object.entries(completeDataByPeriod).map(
      ([key, value]) => ({
        ...value,
        period: key,
      })
    );

    // 複数回投稿されたカテゴリのみを表示する
    const countsByCategory: { [categoryName: string]: number } = Object.values(
      completeDataByPeriod
    ).reduce((acc, periodData) => {
      Object.keys(periodData).forEach((categoryName) => {
        acc[categoryName] = (acc[categoryName] || 0) + 1;
      });
      return acc;
    }, {});

    const categoriesPostedMultipleTimes = Object.entries(countsByCategory)
      .filter(([_, count]) => count > 1)
      .map(([categoryName, _]) => categoryName);

    const categories = Array.from(
      new Set(data.map((d) => d.category.name))
    ).filter((c) => categoriesPostedMultipleTimes.includes(c));

    return { chartData, categories };
  }, [data]);
};

const CategoryButton: FC<{
  category: string;
  visibleCategories: Set<string>;
  toggleCategory: (category: string) => void;
}> = ({ category, visibleCategories, toggleCategory }) => {
  const colorHash = new ColorHash();
  return (
    <button
      onClick={() => toggleCategory(category)}
      style={{
        backgroundColor: visibleCategories.has(category)
          ? colorHash.hex(category)
          : "#ccc",
        border: "none",
        borderRadius: "0.5rem",
        margin: "0.125rem",
      }}
    >
      {category}
    </button>
  );
};

const vhToPx = (vh: number) => Math.round(window.innerHeight / (100 / vh));
const vwToPx = (vw: number) => Math.round(window.innerWidth / (100 / vw));

const FussyPointChart: FC<Props> = ({ data }) => {
  const { chartData, categories } = useChartData(data);
  const { visibleCategories, toggleCategory } =
    useVisibleCategories(categories);
  const isSpMode = useIsSpMode();
  const colorHash = new ColorHash();

  if (categories.length === 0) {
    return (
      <div css={noContentStyle}>
        表示できるデータがありません。詳細は
        <Link
          href={
            "https://fussy.gitbook.io/fussy-user-guide/howtocontribute/getpoints/pointonorepto"
          }
        >
          ポイントの活動レポート
        </Link>
        をご参照ください
      </div>
    );
  }

  if (isSpMode == undefined) {
    return null;
  }

  return (
    <div css={rootStyle}>
      <LineChart
        width={vwToPx(isSpMode ? 90 : 70)}
        height={vhToPx(isSpMode ? 60 : 30)}
        data={chartData}
        margin={
          isSpMode
            ? { top: 5, right: 0, left: 0, bottom: 5 }
            : { top: 5, right: 30, left: 20, bottom: 5 }
        }
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="period" />
        <YAxis />
        <Tooltip />
        <Legend />
        {categories.map(
          (category) =>
            visibleCategories.has(category) && (
              <Line
                type="monotone"
                dataKey={category}
                stroke={colorHash.hex(category)}
                key={category}
                connectNulls
              />
            )
        )}
      </LineChart>
      <div>
        {categories.map((category) => (
          <CategoryButton
            key={category}
            category={category}
            visibleCategories={visibleCategories}
            toggleCategory={toggleCategory}
          />
        ))}
      </div>
    </div>
  );
};

const rootStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  width: 100%;
`;

const noContentStyle = css`
  a {
    text-decoration: underline;
  }
`;

export default FussyPointChart;
